/* eslint-disable max-len */
import { ContentService } from '@blk/cwp-ng-framework';
import * as get from 'lodash/get';
import * as sortBy from 'lodash/sortBy';

import { InsightsTransform } from './insights-transform';

export class TotalRiskInsightsTransform extends InsightsTransform {
	private overviewWithBenchmarkParagraphDCR: string;
	private overviewWithoutBenchmarkParagraphDCR: string;
	private conclusionParagraphDCR: string;

	constructor(public contentSvc: ContentService) {
		super(contentSvc);
	}

	protected setupContent(): void {
		this.overviewWithBenchmarkParagraphDCR = this.contentSvc.get(
			'TOTAL_RISK_INSIGHTS_SEC1_DCR',
			'<p>Based on the current allocations, the portfolios has a total risk of {0} which is <b>{1} {2} its benchmark</b> at {3}. This is driven by <b>{4} equity risk</b> exposure, <b>{5} interest rate risk</b>, and <b>{6} credit spread risk</b> than its benchmark. Differences  in exposure to foreign currency risk and alternatives may also contribute.</p>'
		);
		this.overviewWithoutBenchmarkParagraphDCR = this.contentSvc.get(
			'TOTAL_RISK_INSIGHTS_SEC1_NO_BENCHMARK_DCR',
			'<p>Based on your current allocations, your portfolio has a total risk of {0}. This is <b>primarily driven by {1} risk and {2} risk</b>. Exposure to domestic and foreign stocks, fixed income credit & interest rates, foreign currency, and alternatives may contribute to your total risk.</p>'
		);
		this.conclusionParagraphDCR = this.contentSvc.get(
			'TOTAL_RISK_INSIGHTS_SEC2_DCR',
			'<p>Differences in the amount and type of risk can lead to a difference in the portfolio&#39;s sensitivity to market movements. For example, if US large cap stocks were to fall by <b>{0}</b>, the portfolio could be expected to move by <b>{1}</b>.</p><p><b>Is this in line with your expectations?</b></p>'
		);
	}

	protected getEvaluatedOverview(selectedPortfolioIndex: number): string {
		const totalRiskCell = get(this.insightsTab, 'tableData[0].cells[0][' + selectedPortfolioIndex + ']', {});
		const benchmarkTotalRiskCell = get(this.insightsTab, 'tableData[0].cells[0][' + this.benchmarkIndex + ']', {});

		if (totalRiskCell.rawValue || totalRiskCell.rawValue === 0) {
			const factorData = [];
			for (let idx = 0; idx < get(this.factorsTab, 'tableData[0].headersY.length'); idx++) {
				const cells = get(this.factorsTab, 'tableData[0].cells');
				if (!get(this.factorsTab, 'tableData[0].headersY[' + idx + '].attribute.ignoreInChart')) {
					factorData.push({
						name: get(this.factorsTab, 'tableData[0].headersY[' + idx + '].formattedValue'),
						values: cells && cells[idx] ? cells[idx].map(data => data && data.rawValue) : []
					});
				}
			}

			const equityValues = ['Equity Market', 'Equity Country', 'Equity Sector'];
			let equity: number = 0;
			let rates: number;
			let spreads: number;
			let benchmarkEquity: number = 0;
			let benchmarkRates: number;
			let benchmarkSpreads: number;
			factorData.forEach((data, index) => {
				const selectedValue = data.values[selectedPortfolioIndex];
				const benchmarkValue = data.values[this.benchmarkIndex];
				if (equityValues.findIndex(value => value.toLowerCase() === data.name.toLowerCase()) !== -1) {
					equity += selectedValue;
					benchmarkEquity += benchmarkValue;
				} else if (data.name.toLowerCase() === 'rates') {
					rates = selectedValue;
					benchmarkRates = benchmarkValue;
				} else if (data.name.toLowerCase() === 'spreads') {
					spreads = selectedValue;
					benchmarkSpreads = benchmarkValue;
				}
			});

			if (this.benchmarkIndex !== -1) {
				let benchmarkVolatility = this.translate('RISK_INSIGHTS_BENCHMARK_VOLATILITY_LEVEL_1', 'meaningfully higher', this.contentSvc);
				const volatilityPercent: number = Number(this.getDiffPercentageFromBenchmark(totalRiskCell.rawValue, benchmarkTotalRiskCell.rawValue));

				if (volatilityPercent < -8) {
					benchmarkVolatility = this.translate('RISK_INSIGHTS_BENCHMARK_VOLATILITY_LEVEL_5', 'meaningfully lower', this.contentSvc);
				} else if (volatilityPercent >= -8 && volatilityPercent < -2) {
					benchmarkVolatility = this.translate('RISK_INSIGHTS_BENCHMARK_VOLATILITY_LEVEL_4', 'modestly lower', this.contentSvc);
				} else if (volatilityPercent >= -2 && volatilityPercent <= 2) {
					benchmarkVolatility = this.translate('RISK_INSIGHTS_BENCHMARK_VOLATILITY_LEVEL_3', 'effectively inline', this.contentSvc);
				} else if (volatilityPercent > 2 && volatilityPercent <= 8) {
					benchmarkVolatility = this.translate('RISK_INSIGHTS_BENCHMARK_VOLATILITY_LEVEL_2', 'modestly higher', this.contentSvc);
				}

				const BENCHMARK_VOL_LEVEL_INLINE = this.translate('RISK_INSIGHTS_BENCHMARK_VOLATILITY_LEVEL_3', 'effectively inline', this.contentSvc);
				return isNaN(volatilityPercent)
					? null
					: this.overviewWithBenchmarkParagraphDCR
						.replace('{0}', totalRiskCell.formattedValue)
						.replace('{1}', benchmarkVolatility)
						.replace(
							'{2}',
							benchmarkVolatility === BENCHMARK_VOL_LEVEL_INLINE
								? this.translate('RISK_INSIGHTS_BENCHMARK_VOLATILITY_WITH_DESC', 'with', this.contentSvc)
								: this.translate('RISK_INSIGHTS_BENCHMARK_VOLATILITY_THAN_DESC', 'than', this.contentSvc)
						)
						.replace('{3}', benchmarkTotalRiskCell.formattedValue)
						.replace('{4}', this.getCompareNumberDescription(equity, benchmarkEquity, 5))
						.replace('{5}', this.getCompareNumberDescription(rates, benchmarkRates, 5))
						.replace('{6}', this.getCompareNumberDescription(spreads, benchmarkSpreads, 5));
			} else {
				const idx = selectedPortfolioIndex;
				const factorDataSorted = sortBy(factorData, [data => data.values[idx]]);
				return totalRiskCell.rawValue || totalRiskCell.rawValue === 0
					? this.overviewWithoutBenchmarkParagraphDCR
						.replace('{0}', totalRiskCell.formattedValue)
						.replace(
							'{1}',
							factorDataSorted && factorDataSorted.length > 0 && factorDataSorted[factorData.length - 1] ? factorDataSorted[factorData.length - 1].name : 'n/a'
						)
						.replace(
							'{2}',
							factorDataSorted && factorDataSorted.length > 1 && factorDataSorted[factorData.length - 2] ? factorDataSorted[factorData.length - 2].name : 'n/a'
						)
					: null;
			}
		} else {
			return null;
		}
	}

	protected getEvaluatedConclusion(selectedPortfolioIndex: number): string {
		const downShock = '15%';
		const pnlCell = get(this.insightsTab, 'tableData[0].cells[2][' + selectedPortfolioIndex + ']');

		return downShock && (pnlCell && (pnlCell.rawValue || pnlCell.rawValue === 0))
			? this.conclusionParagraphDCR.replace('{0}', downShock).replace('{1}', pnlCell.formattedValue)
			: undefined;
	}
}
